/**
 * Two-layer collection sort.
 *
 * Layer 1 (primary): whatever sort_by the shopper picks in the dropdown
 * (Price, Best selling, Alphabetical, etc.) — handled natively by Shopify,
 * unchanged.
 *
 * Layer 2 (secondary): our own ranking (NP products first, then most
 * reviews, then source-store best-selling rank) is applied as a tiebreak
 * within whatever the primary sort produced. For sort modes where Shopify
 * has no real per-product signal to differentiate most products in this
 * catalog (best selling, most relevant, date created), the entire grid is
 * treated as one tie group, so our ranking effectively becomes the order.
 * Price and alphabetical sorts are left fully intact since they carry a
 * real, customer-meaningful primary value.
 */
(() => {
  const RANK_JSON_URL = window.__productRankUrl;
  if (!RANK_JSON_URL) return;

  let rankMapPromise = null;
  function loadRankMap() {
    if (!rankMapPromise) {
      rankMapPromise = fetch(RANK_JSON_URL)
        .then((r) => r.json())
        .catch(() => ({}));
    }
    return rankMapPromise;
  }

  function getSortBy() {
    return new URLSearchParams(window.location.search).get('sort_by') || '';
  }

  const FULL_OVERRIDE_MODES = new Set([
    'best-selling',
    'most-relevant',
    'created-descending',
    'created',
    'relevance',
  ]);
  const PRICE_MODES = new Set(['price-ascending', 'price-descending']);

  async function reorderGrid(grid) {
    const sortBy = getSortBy();
    if (sortBy === '' || sortBy === 'manual') return;
    if (!FULL_OVERRIDE_MODES.has(sortBy) && !PRICE_MODES.has(sortBy)) return;

    const rankMap = await loadRankMap();
    const items = Array.from(grid.querySelectorAll(':scope > li[data-product-id]'));
    if (items.length < 2) return;

    const withMeta = items.map((li, domIndex) => ({
      li,
      domIndex,
      rank: rankMap[li.dataset.productId] ?? Number.MAX_SAFE_INTEGER,
      price: PRICE_MODES.has(sortBy) ? Number(li.dataset.price || NaN) : null,
    }));

    withMeta.sort((a, b) => {
      if (PRICE_MODES.has(sortBy)) {
        if (a.price !== b.price && !Number.isNaN(a.price) && !Number.isNaN(b.price)) {
          return a.price - b.price;
        }
      }
      if (a.rank !== b.rank) return a.rank - b.rank;
      return a.domIndex - b.domIndex;
    });

    const newOrder = withMeta.map((m) => m.li);
    const unchanged = newOrder.every((li, i) => li === items[i]);
    if (unchanged) return;

    grid.append(...newOrder);
  }

  function reorderAllGrids() {
    document.querySelectorAll('ul.product-grid').forEach((grid) => {
      reorderGrid(grid);
    });
  }

  let observing = true;
  const observer = new MutationObserver((mutations) => {
    if (!observing) return;
    const relevant = mutations.some((m) =>
      Array.from(m.addedNodes).some(
        (n) => n.nodeType === 1 && (n.matches?.('li.product-grid__item') || n.querySelector?.('li.product-grid__item'))
      )
    );
    if (!relevant) return;
    observing = false;
    Promise.resolve(reorderAllGrids()).finally(() => {
      observing = true;
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reorderAllGrids);
  } else {
    reorderAllGrids();
  }
})();
